import { salesApiService } from '../api/salesApi';

/** Max records to load for print/PDF to avoid freezing the browser on huge datasets. */
export const MAX_SALES_CALL_PRINT_RECORDS = 1000;

/** Max PDF emails queued in one "today's records" action (server + inbox safety). */
export const MAX_EMAIL_PDF_TODAY_BATCH = 50;

/**
 * Local calendar "today" start/end as ISO strings for API `created_at__gte` / `created_at__lte`.
 */
export function getLocalTodayCreatedAtIsoRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return {
    created_at__gte: start.toISOString(),
    created_at__lte: end.toISOString(),
  };
}

/**
 * Loads all sales calls matching the same filters as the list, paginating until done or cap.
 * Results are sorted by created_at ascending (record date).
 *
 * @param {Record<string, unknown>} filters - Same shape as SalesCallPage filters + page omitted.
 * @param {{ maxRecords?: number }} [options]
 * @returns {Promise<{ calls: object[], truncated: boolean, totalCount: number }>}
 */
export async function fetchAllSalesCallsMatchingFilters(filters = {}, options = {}) {
  const maxRecords = options.maxRecords ?? MAX_SALES_CALL_PRINT_RECORDS;
  const merged = [];
  let page = 1;
  let totalCount = 0;

  while (merged.length < maxRecords) {
    const response = await salesApiService.getSalesCalls({ ...filters, page });
    if (!response?.success) {
      const err = new Error(response?.message || 'Failed to load sales calls');
      err.code = 'LOAD_FAILED';
      throw err;
    }
    const batch = Array.isArray(response.data) ? response.data : [];
    totalCount = response.pagination?.count ?? merged.length + batch.length;

    for (const row of batch) {
      merged.push(row);
      if (merged.length >= maxRecords) break;
    }

    const next = response.pagination?.next;
    if (!next || batch.length === 0) break;
    page += 1;
  }

  const truncated = totalCount > merged.length;
  const calls = [...merged].sort((a, b) => {
    const t1 = a.created_at ? new Date(a.created_at).getTime() : 0;
    const t2 = b.created_at ? new Date(b.created_at).getTime() : 0;
    return t1 - t2;
  });

  return { calls, truncated, totalCount };
}
